import { GetContract } from "@/services/fhir/contract/GetContract";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useOrganizationContext } from "./OrganizationContext";
import { Contract } from "fhir/r5";

type ContractResource = Contract;

interface HealthPlanContractContextValue {
  contractHealthPlan: ContractResource | null;
  refetchContract: () => Promise<void>;
  loading: boolean;
}

interface HealthPlanContractContextProviderProps {
  children: React.ReactNode;
}

export const HealthPlanContractContext =
  createContext<HealthPlanContractContextValue>(
    {} as HealthPlanContractContextValue
  );

export function HealthPlanContractContextProvider({
  children,
}: HealthPlanContractContextProviderProps) {
  const { organization } = useOrganizationContext();
  const [contractHealthPlan, setContractHealthPlan] =
    useState<ContractResource | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchContract = useCallback(async () => {
    setLoading(true);
    try {
      if (!organization?.id) {
        console.warn("Nenhuma organização selecionada");
        setContractHealthPlan(null);
        return;
      }

      const contractData = await GetContract({
        organization: organization.id,
        count: 1,
        sort: true,
        status: "executed",
        identifier: "healthPlan",
      });

      if (contractData?.entry?.[0]?.resource) {
        const resource = contractData.entry?.[0]?.resource;
        setContractHealthPlan(resource);
      } else {
        setContractHealthPlan(null);
      }
    } catch (error) {
      console.error("Erro ao buscar contrato de plano de saúde:", error);
      setContractHealthPlan(null);
    } finally {
      setLoading(false);
    }
  }, [organization]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const contextValue: HealthPlanContractContextValue = {
    loading,
    contractHealthPlan,
    refetchContract: fetchContract,
  };

  return (
    <HealthPlanContractContext.Provider value={contextValue}>
      {children}
    </HealthPlanContractContext.Provider>
  );
}

export function useHealthPlanContractContext() {
  return useContext(HealthPlanContractContext);
}
